// src/pages/Admin/views/verification-rules/BannedPhrasesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  listBannedPhrases,
  batchUpdateBannedPhrases
} from './verificationRulesApi'; // Ensure path is correct
import CrudTable from '../../../../components/Admin/VerificationRules/CrudTable'; // Adjust path
import { formatDateTime } from '../../../../utility/dateFormatter'; // formatDate not needed for phrases
import { Button } from '../../../../components/ui/button'; // Adjust path
import { useToast } from '../../../../hooks/use-toast';

const PHRASE_TYPES = {
  VULGARITY: "VULGARITY",
  CONTACT_INFO: "CONTACT_INFO",
  FORBIDDEN_TERM: "FORBIDDEN_TERM",
  TEST_DEMO_STRING: "TEST_DEMO_STRING",
};

const PHRASE_TYPE_LABELS = {
  [PHRASE_TYPES.VULGARITY]: "Vulgarity",
  [PHRASE_TYPES.CONTACT_INFO]: "Contact Info",
  [PHRASE_TYPES.FORBIDDEN_TERM]: "Forbidden Terms",
  [PHRASE_TYPES.TEST_DEMO_STRING]: "Test/Demo Strings",
};

const BannedPhrasesPage = () => {
  const [activeTab, setActiveTab] = useState(PHRASE_TYPES.VULGARITY);
  // Store phrases per type: { VULGARITY: [], CONTACT_INFO: [], ... }
  const [phrasesByType, setPhrasesByType] = useState({
    [PHRASE_TYPES.VULGARITY]: [],
    [PHRASE_TYPES.CONTACT_INFO]: [],
    [PHRASE_TYPES.FORBIDDEN_TERM]: [],
    [PHRASE_TYPES.TEST_DEMO_STRING]: [],
  });

  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stagedChanges, setStagedChanges] = useState([]); // Global for all phrase types

  const [isAdding, setIsAdding] = useState(false);
  const [newItemData, setNewItemData] = useState({
    phrase: '',
    phrase_type: activeTab, // Defaults to the current active tab
    notes: '',
    is_fatal: true,
    is_active: true,
  });

  const [editingItemId, setEditingItemId] = useState(null); // Can be id (number) or tempId (string)
  const [currentItemDataForEdit, setCurrentItemDataForEdit] = useState(null);

  const fetchDataForTab = useCallback(async (tabKey) => {
    setIsLoading(true);
    setError(null);
    try {
      // When fetching for a tab, we only want to update that tab's data.
      // Staged changes should persist across tab switches.
      const response = await listBannedPhrases(tabKey);
      setPhrasesByType(prevPhrases => ({
        ...prevPhrases,
        [tabKey]: response.data || [],
      }));
    } catch (err) {
      const errorMessage = `Failed to fetch ${PHRASE_TYPE_LABELS[tabKey]}.`;
      setError(errorMessage);
      toast({
          variant: "destructive",
          description: errorMessage
        });
      console.error("Fetch Error for tab", tabKey, err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDataForTab(activeTab);
  }, [activeTab, fetchDataForTab]);


  // Effect to clear edit mode if item not found in current tab (e.g., after save and refetch)
  useEffect(() => {
    if (editingItemId) {
        const currentTabPhrases = phrasesByType[activeTab] || [];
        if (!currentTabPhrases.find(item => (item.id || item.tempId) === editingItemId)) {
            // Also check staged changes if it's a new item being edited that hasn't hit backend yet
            const stagedItem = stagedChanges.find(sc => sc.tempId === editingItemId && sc.phrase_type === activeTab);
            if(!stagedItem) {
                handleCancelEdit();
            }
        }
    }
  }, [phrasesByType, activeTab, editingItemId, stagedChanges]);


  const handleTabChange = (tabKey) => {
    if (isLoading) return; // Prevent tab change while loading
    handleCancelEdit(); // Cancel any ongoing edit on the current tab
    setIsAdding(false);  // Close add form if open
    setActiveTab(tabKey);
    setNewItemData(prev => ({ // Reset new item form, defaulting to new tab's type
        ...prev,
        phrase: '',
        notes: '',
        phrase_type: tabKey, // CRITICAL: set phrase_type for new items based on tab
        is_fatal: true,
        is_active: true,
    }));
  };

  const stageChange = (itemDataWithChange) => {
    setStagedChanges(prevChanges => {
      const keyOfChangedItem = itemDataWithChange.id || itemDataWithChange.tempId;
      let itemExistsInStaged = false;
      const updatedChanges = prevChanges.map(existingChange => {
        const keyOfExistingChange = existingChange.id || existingChange.tempId;
        if (keyOfExistingChange === keyOfChangedItem) {
          itemExistsInStaged = true;
          return { ...existingChange, ...itemDataWithChange };
        }
        return existingChange;
      });
      if (!itemExistsInStaged) {
        return [...updatedChanges, { ...itemDataWithChange }];
      }
      return updatedChanges;
    });
  };

  const handleEditClick = (item) => {
    const currentItemStateFromTab = phrasesByType[activeTab].find(i => (i.id || i.tempId) === (item.id || item.tempId)) ||
                                   stagedChanges.find(sc => sc.tempId === item.tempId && sc.phrase_type === activeTab) || // For new items being edited
                                   item;


    if (editingItemId === (currentItemStateFromTab.id || currentItemStateFromTab.tempId)) {
      handleCancelEdit();
    } else {
      setEditingItemId(currentItemStateFromTab.id || currentItemStateFromTab.tempId);
      const dataForEditing = { ...currentItemStateFromTab };

      const stagedVersion = stagedChanges.find(sc => (sc.id || sc.tempId) === (currentItemStateFromTab.id || currentItemStateFromTab.tempId));
      if (stagedVersion) {
        setCurrentItemDataForEdit({ ...dataForEditing, ...stagedVersion });
      } else {
        setCurrentItemDataForEdit(dataForEditing);
      }
    }
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setCurrentItemDataForEdit(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const L_value = type === 'checkbox' ? checked : value;
    if (editingItemId) {
      setCurrentItemDataForEdit(prev => ({ ...prev, [name]: L_value }));
    } else if (isAdding) {
      setNewItemData(prev => ({ ...prev, [name]: L_value }));
    }
  };

  const applyRowEditChanges = () => {
    if (!editingItemId || !currentItemDataForEdit) return;
    if (!currentItemDataForEdit.phrase?.trim()) {      
      toast({
          variant: "destructive",
          description: "Phrase text cannot be empty when editing.",
        });
      return;
    }
    setError(null);

    const editedData = { ...currentItemDataForEdit };
    editedData.isNew = !!(editedData.tempId && !editedData.id); // Correctly determine if new
     if (editedData.id && typeof editedData.id === 'number') { // Persisted item
        delete editedData.tempId;
    }


    // Optimistically update the specific tab's list
    setPhrasesByType(prevData => ({
      ...prevData,
      [activeTab]: prevData[activeTab].map(phrase =>
        (phrase.id === editingItemId || phrase.tempId === editingItemId)
          ? { ...phrase, ...editedData }
          : phrase
      ),
    }));
    stageChange(editedData);
    toast({
          variant: "success",
          description: `Changes for the row applied. Click "Save All Changes" to persist`,
        });
  };

  const handleAddNewRowToList = () => {
    const phraseInput = newItemData.phrase.trim();
    if (!phraseInput) {
      toast({
          variant: "destructive",
          description: "Phrase text is required.",
        });
      return;
    }
    setError(null);

    const tempId = `temp-phrase-${Date.now()}`;
    const newPhraseEntry = {
      ...newItemData, // Includes phrase_type from activeTab
      phrase: phraseInput,
      tempId: tempId,
      isNew: true,
    };
    delete newPhraseEntry.id;

    // Add to UI list for the current tab and stage it
    setPhrasesByType(prevData => ({
        ...prevData,
        [activeTab]: [newPhraseEntry, ...(prevData[activeTab] || [])]
    }));
    stageChange(newPhraseEntry);

    setNewItemData({ phrase: '', phrase_type: activeTab, notes: '', is_fatal: true, is_active: true });
    setIsAdding(false);
    toast({
          variant: "success",
          description: 'Phrase added to list for this tab. Click "Save All Changes" to persist.',
        });
  };

  const handleDeleteOrDeactivate = (itemToToggle) => {
    const itemCurrentState = phrasesByType[activeTab].find(i => (i.id || i.tempId) === (itemToToggle.id || itemToToggle.tempId)) ||
                           stagedChanges.find(sc => sc.tempId === itemToToggle.tempId && sc.phrase_type === activeTab) ||
                           itemToToggle;

    if (window.confirm(`Are you sure you want to ${itemCurrentState.is_active ? 'deactivate' : 'activate'} phrase "${itemCurrentState.phrase || 'this item'}"? This will be saved on "Save All".`)) {
      setError(null);
      const newActiveState = !itemCurrentState.is_active;

      // Optimistically update UI for the current tab
      setPhrasesByType(prevData => ({
        ...prevData,
        [activeTab]: prevData[activeTab].map(phrase =>
          (phrase.id === itemCurrentState.id || phrase.tempId === itemCurrentState.tempId)
            ? { ...phrase, is_active: newActiveState }
            : phrase
        ),
      }));

      const changeToStage = {
        ...itemCurrentState, // Start with current state
        phrase: itemCurrentState.phrase.trim(),
        is_active: newActiveState,
        isNew: !itemCurrentState.id,
        // phrase_type is already part of itemCurrentState
      };
       if (changeToStage.isNew) delete changeToStage.id;
       else delete changeToStage.tempId;

      stageChange(changeToStage);

      if (editingItemId === (itemCurrentState.id || itemCurrentState.tempId)) {
        setCurrentItemDataForEdit(prev => ({ ...prev, is_active: newActiveState }));
      }
      toast({
          variant: "success",
          description: `Phrase marked for ${newActiveState ? 'activation' : 'deactivation'}. Click "Save All Changes".`,
        });
    }
  };

  const handleSaveAll = async () => {
    if (editingItemId && currentItemDataForEdit) {
      applyRowEditChanges();
      if (error) { // Check if applyRowEditChanges set an error (validation)
          
          toast({
          variant: "destructive",
          description: "Cannot save, please fix errors in the row being edited."
        });
          return;
      }
      handleCancelEdit();
    }
    if (stagedChanges.length === 0) {
      toast({
          variant: "success",
          description: "No changes to save.",
        });
      return;
    }
    setIsLoading(true);
    setError(null);
    const payload = stagedChanges.map(change => {
      const { isNew, tempId, _isDeleted, created_at, updated_at, id, ...itemData } = change;
      // phrase_type must be in itemData
      if (!itemData.phrase_type) {
          console.error("Error: phrase_type missing in staged change for item:", itemData);
          // Potentially skip this item or throw error
      }
      if (isNew || !id || typeof id !== 'number') {
        // No 'id' for new items
      } else {
        itemData.id = id;
      }
      return itemData;
    });

    try {
      // console.log("Sending Banned Phrases to batch update:", JSON.stringify(payload, null, 2));
      const response = await batchUpdateBannedPhrases(payload);
      
      toast({
          variant: "success",
          description: `Batch update successful! ${response.data?.length || 0} phrases processed.`,
        });
      setStagedChanges([]);
      // Refetch data for the current tab. For full consistency, one might refetch all tabs or trust the backend response.
      fetchDataForTab(activeTab);
    } catch (err) {
      let errorMessage = "An unexpected error occurred during phrase batch update.";
      if (err.response && err.response.data) { /* ... (error parsing) ... */ }
      setError(errorMessage);
      toast({
          variant: "destructive",
          description: errorMessage
        });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      key: 'phrase', header: 'Phrase Text',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="text" name="phrase" value={currentItemDataForEdit?.phrase || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.phrase}</span>
    },
    { // phrase_type is determined by tab, not directly editable in row (could be made so if needed)
      key: 'phrase_type_display', header: 'Type',
      render: (item) => <span className="text-sm">{PHRASE_TYPE_LABELS[item.phrase_type] || item.phrase_type}</span>
    },
    {
      key: 'notes', header: 'Notes',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <textarea name="notes" value={currentItemDataForEdit?.notes || ''} onChange={handleInputChange} rows={2} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.notes || '-'}</span>
    },
    {
      key: 'is_fatal', header: 'Fatal?',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="checkbox" name="is_fatal" checked={!!currentItemDataForEdit?.is_fatal} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"/> :
        <span className="text-sm">{item.is_fatal ? 'Yes' : 'No'}</span>
    },
    {
      key: 'is_active', header: 'Status',
      render: (item) => editingItemId === (item.id || item.tempId) ?
         ( <label className="flex items-center text-sm">
            <input type="checkbox" name="is_active" checked={!!currentItemDataForEdit?.is_active} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/>
            {currentItemDataForEdit?.is_active ? "Active" : "Inactive"}
          </label>
        ) :
        <span className={`text-sm font-semibold px-2 py-1 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
    },
    { key: 'created_at', header: 'Created', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">New</span> : formatDateTime(item.created_at)}</span> },
    { key: 'updated_at', header: 'Updated', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">N/A</span> : formatDateTime(item.updated_at)}</span> },
  ];

  // Optimistically combine fetched data with staged changes for the current tab display
  const currentTabDisplayPhrases = useCallback(() => {
    const basePhrases = phrasesByType[activeTab] || [];
    const baseMap = new Map(basePhrases.map(p => [p.id || p.tempId, {...p}]));

    stagedChanges.forEach(change => {
        if (change.phrase_type === activeTab) { // Only consider changes relevant to the active tab
            const key = change.id || change.tempId;
            baseMap.set(key, { ...(baseMap.get(key) || {}), ...change });
        }
    });
    return Array.from(baseMap.values());
  }, [phrasesByType, stagedChanges, activeTab]);


  const activePhrases = currentTabDisplayPhrases().filter(p => p.is_active);
  const inactivePhrases = currentTabDisplayPhrases().filter(p => !p.is_active);
  const showSaveAllButton = stagedChanges.length > 0 || editingItemId !== null;

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
        Manage Banned Phrases
      </h2>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-300 flex space-x-1">
        {Object.values(PHRASE_TYPES).map((type) => (
          <button
            key={type}
            onClick={() => handleTabChange(type)}
            className={`py-2 px-4 text-sm font-medium focus:outline-none -mb-px
              ${activeTab === type
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-400'
              }`}
            disabled={isLoading}
          >
            {PHRASE_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-blue-600 animate-pulse">Loading {PHRASE_TYPE_LABELS[activeTab]}...</p>}
      {error && ( <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded whitespace-pre-line"> {typeof error === 'string' ? error : JSON.stringify(error, null, 2)} </div> )}

      {!isAdding && (
        <Button onClick={() => { setIsAdding(true); setError(null); handleCancelEdit(); }} variant="primary" size="md" className="mb-6">
          Add New {PHRASE_TYPE_LABELS[activeTab]} Phrase
        </Button>
      )}

      {isAdding && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 shadow">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Add New Banned Phrase ({PHRASE_TYPE_LABELS[activeTab]})</h4>
           <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="new_phrase_text" className="block mb-1 text-sm font-medium text-gray-600">Phrase Text*</label>
              <input id="new_phrase_text" type="text" name="phrase" placeholder="Enter phrase text" value={newItemData.phrase} onChange={handleInputChange} required className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"/>
            </div>
            <div> {/* Phrase type is fixed by tab for new items */}
                <label className="block mb-1 text-sm font-medium text-gray-600">Phrase Type</label>
                <input type="text" value={PHRASE_TYPE_LABELS[newItemData.phrase_type]} disabled className="p-2 border border-gray-300 rounded-md w-full bg-gray-100"/>
            </div>
            <div>
              <label htmlFor="new_phrase_notes" className="block mb-1 text-sm font-medium text-gray-600">Notes</label>
              <textarea id="new_phrase_notes" name="notes" rows={2} placeholder="Optional notes" value={newItemData.notes} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"/>
            </div>
             <div className="flex items-center space-x-6 mt-2">
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_fatal" checked={newItemData.is_fatal} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Fatal
              </label>
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_active" checked={newItemData.is_active} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Active
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-x-3">
            <Button onClick={handleAddNewRowToList} variant="green" size="md">Add to List & Stage</Button>
            <Button onClick={() => { setIsAdding(false); setError(null); }} variant="outline" size="md">Cancel</Button>
          </div>
        </div>
      )}

      {showSaveAllButton && (
        <div className="my-6 text-right">
          <Button onClick={handleSaveAll} variant="primary" size="lg" isLoading={isLoading} disabled={isLoading || (editingItemId && !currentItemDataForEdit?.phrase)}>
            {isLoading ? 'Saving All...' : `Save All (${stagedChanges.length}) Changes`}
          </Button>
        </div>
      )}

      <section className="mb-8">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Active {PHRASE_TYPE_LABELS[activeTab]} ({activePhrases.length})</h3>
        {activePhrases.length > 0 ? (
            <CrudTable items={activePhrases} columns={columns} onDeleteItem={handleDeleteOrDeactivate} editingItemId={editingItemId} onEditItem={handleEditClick} onCancelEdit={handleCancelEdit} onApplyEdit={applyRowEditChanges} /* ... */ />
        ) : (<p className="text-gray-500">No active phrases in this category.</p>)}
      </section>

      <section>
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Inactive {PHRASE_TYPE_LABELS[activeTab]} ({inactivePhrases.length})</h3>
         {inactivePhrases.length > 0 ? (
            <CrudTable items={inactivePhrases} columns={columns} onDeleteItem={handleDeleteOrDeactivate} editingItemId={editingItemId} onEditItem={handleEditClick} onCancelEdit={handleCancelEdit} onApplyEdit={applyRowEditChanges} /* ... */ />
         ) : (<p className="text-gray-500">No inactive phrases in this category.</p>)}
      </section>
    </div>
  );
};

export default BannedPhrasesPage;